/**
 * Revenue Mix Over Time Chart (Highcharts)
 * 100% stacked area chart showing revenue mix by master channel
 */
import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getChannelColorVar } from '../../../utils/channelColorUtils.js';

export default function RevenueMixChart({ data = [], masterChannels = [] }) {
  const options = useMemo(() => {
    if (!data || data.length === 0 || !masterChannels || masterChannels.length === 0) {
      return {
        title: { text: 'Revenue Mix Over Time' },
        subtitle: { text: 'No data available for this period/channel' },
        series: [],
      };
    }

    // Build series for each master channel
    // Use shared strict resolver for colors (single source of truth) - do not use backend color_hex
    const series = masterChannels.map(mc => ({
      name: mc.name,
      data: data.map(d => d[mc.code] || 0),
      color: getChannelColorVar(mc.code || mc.master_channel_key), // Use shared resolver, not backend color_hex
    })).filter(s => s.data.some(v => v > 0)); // Only include channels with data

    return {
      chart: {
        type: 'area',
        height: 350,
      },
      title: {
        text: 'Revenue Mix Over Time',
        style: { fontSize: '16px', fontWeight: '600' },
      },
      subtitle: {
        text: 'Percentage of revenue by channel (100% stacked). ℹ︎ Shows share, not absolute growth.',
        style: { fontSize: '12px', color: '#666' },
      },
      xAxis: {
        categories: data.map(d => d.month),
        title: { text: 'Month' },
      },
      yAxis: {
        title: { text: 'Percentage (%)' },
        max: 100,
        labels: {
          formatter: function() {
            return this.value + '%';
          },
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: '#ffffff', // Fully opaque white background
        borderColor: '#e5e7eb',
        borderWidth: 1,
        style: {
          color: '#111827', // Dark text for readability
        },
        formatter: function() {
          let tooltip = '<b>' + this.x + '</b><br/>';
          this.points.forEach(point => {
            tooltip += '<span style="color:' + point.color + '">●</span> ' + point.series.name + ': <b>' + 
              point.y.toFixed(1) + '%</b><br/>';
          });
          return tooltip;
        },
      },
      plotOptions: {
        area: {
          stacking: 'percent',
          lineColor: '#ffffff',
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: '#ffffff',
          },
        },
      },
      series,
      credits: { enabled: false },
    };
  }, [data, masterChannels]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
